
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Phone, Mail, MapPin, Leaf, Snowflake, Home, Building2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { getTownBySlug, townData } from '@/data/townData';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TownPage = () => {
  const { townSlug } = useParams<{ townSlug: string }>();
  const town = getTownBySlug(townSlug || '');

  if (!town) {
    return <Navigate to="/locations" replace />;
  }

  // Services offered
  const services = [
    { title: 'Landscape Maintenance', description: 'Weekly lawn care, mowing, edging, and seasonal programs', icon: Leaf },
    { title: 'Landscape Design', description: 'Custom designs that enhance your property\'s natural beauty', icon: Home },
    { title: 'Hardscaping', description: 'Patios, walkways, retaining walls, and outdoor living spaces', icon: Building2 },
    { title: 'Snow Removal', description: 'Reliable winter services for safe property access', icon: Snowflake },
  ];

  // Related towns for internal linking
  const relatedTowns = townData
    .filter(t => t.slug !== town.slug && t.county === town.county)
    .slice(0, 4);

  // JSON-LD Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LandscapingBusiness",
    "name": "PK Landscape Associates",
    "description": `Professional landscaping services in ${town.name}, CT. ${town.metaDescription}`,
    "url": `https://pklandscapect.com/locations/${town.slug}`,
    "telephone": "{{PHONE}}",
    "email": "{{EMAIL}}",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": town.name,
      "addressRegion": "CT",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "City",
      "name": town.name,
      "containedInPlace": {
        "@type": "State",
        "name": "Connecticut"
      }
    },
    "serviceType": ["Landscaping", "Lawn Care", "Hardscaping", "Snow Removal", "Irrigation"],
    "priceRange": "$$-$$$"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": town.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{town.metaTitle}</title>
        <meta name="description" content={town.metaDescription} />
        <link rel="canonical" href={`https://pklandscapect.com/locations/${town.slug}`} />
        <meta property="og:title" content={town.metaTitle} />
        <meta property="og:description" content={town.metaDescription} />
        <meta property="og:url" content={`https://pklandscapect.com/locations/${town.slug}`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-pkgreen-900 via-pkgreen-800 to-pkgreen-700">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <nav className="flex items-center gap-2 text-pkgreen-200 text-sm mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <Link to="/locations" className="hover:text-white transition-colors">Locations</Link>
              <span>/</span>
              <span className="text-white">{town.name}, CT</span>
            </nav>
            
            <span className="inline-block bg-pkgreen-600/50 text-pkgreen-100 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              {town.county} County, Connecticut
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
              {town.h1}
            </h1>
            
            <p className="text-xl text-pkgreen-100 mb-8 max-w-3xl leading-relaxed">
              {town.heroText}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-white text-pkgreen-800 hover:bg-pkgreen-50 py-3 px-8 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
              >
                Request a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="tel:2035551234"
                className="bg-pkgreen-600/30 border border-pkgreen-400/50 text-white hover:bg-pkgreen-600/50 py-3 px-8 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-pkstone-900 mb-4">
              Our {town.name} Landscaping Services
            </h2>
            <p className="text-pkstone-600 max-w-2xl mx-auto">
              {town.commercialFocus 
                ? `Comprehensive residential and commercial landscaping for ${town.name} properties.`
                : `Premium residential landscaping services tailored for ${town.name} homeowners.`
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {services.map((service, index) => (
              <div key={index} className="bg-pkstone-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <service.icon className="h-10 w-10 text-pkgreen-600 mb-4" />
                <h3 className="text-xl font-serif font-bold text-pkstone-900 mb-2">{service.title}</h3>
                <p className="text-pkstone-600 text-sm">{service.description}</p>
              </div>
            ))}
          </div>

          {/* Service Highlights from town data */}
          <div className="bg-pkgreen-50 rounded-xl p-8">
            <h3 className="text-2xl font-serif font-bold text-pkstone-900 mb-6">
              What Sets Us Apart in {town.name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {town.serviceHighlights.map((highlight, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-pkgreen-600 flex-shrink-0 mt-0.5" />
                  <span className="text-pkstone-700">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Property Types & Seasonal Needs */}
      <section className="py-16 bg-pkstone-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Property Types */}
            <div>
              <h2 className="text-2xl font-serif font-bold text-pkstone-900 mb-6">
                Properties We Serve in {town.name}
              </h2>
              <div className="space-y-4">
                {town.propertyTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                    <div className="w-2 h-2 bg-pkgreen-600 rounded-full"></div>
                    <span className="text-pkstone-700">{type}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Seasonal Needs */}
            <div>
              <h2 className="text-2xl font-serif font-bold text-pkstone-900 mb-6">
                Seasonal Considerations for {town.name}
              </h2>
              <div className="space-y-4">
                {town.seasonalNeeds.map((need, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                    <div className="w-2 h-2 bg-pkgreen-600 rounded-full"></div>
                    <span className="text-pkstone-700">{need}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-8 text-center">
              Frequently Asked Questions About Landscaping in {town.name}
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {town.faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="bg-pkstone-50 rounded-lg px-6 border-none"
                >
                  <AccordionTrigger className="text-left font-medium text-pkstone-900 hover:text-pkgreen-700 py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-pkstone-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-pkgreen-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Ready to Transform Your {town.name} Property?
          </h2>
          <p className="text-pkgreen-100 mb-8 max-w-2xl mx-auto">
            Contact us today for a consultation. We'll assess your property and provide a customized proposal tailored to your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-pkgreen-800 hover:bg-pkgreen-50 py-3 px-8 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
            >
              <Mail className="mr-2 h-5 w-5" />
              Request Consultation
            </Link>
            <a
              href="tel:2035551234"
              className="bg-pkgreen-700 border border-pkgreen-600 text-white hover:bg-pkgreen-600 py-3 px-8 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (203) 555-1234
            </a>
          </div>
        </div>
      </section>

      {/* Related Locations */}
      {relatedTowns.length > 0 && (
        <section className="py-16 bg-pkstone-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-serif font-bold text-pkstone-900 mb-8 text-center">
              Also Serving Nearby {town.county} County Towns
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedTowns.map((related) => (
                <Link
                  key={related.slug}
                  to={`/locations/${related.slug}`}
                  className="bg-white p-4 rounded-lg text-center hover:shadow-md transition-shadow group"
                >
                  <MapPin className="h-6 w-6 text-pkgreen-600 mx-auto mb-2 group-hover:text-pkgreen-700" />
                  <span className="text-pkstone-800 font-medium group-hover:text-pkgreen-700">
                    {related.name}, CT
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/locations"
                className="text-pkgreen-600 hover:text-pkgreen-700 font-medium inline-flex items-center"
              >
                View All Service Areas
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Internal Links to Services */}
      <section className="py-12 bg-white border-t border-pkstone-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/services/lawn-maintenance" className="text-pkgreen-600 hover:text-pkgreen-700">
              Lawn Maintenance in {town.name}
            </Link>
            <span className="text-pkstone-300">|</span>
            <Link to="/services/landscape-design" className="text-pkgreen-600 hover:text-pkgreen-700">
              Landscape Design in {town.name}
            </Link>
            <span className="text-pkstone-300">|</span>
            <Link to="/services/hardscaping" className="text-pkgreen-600 hover:text-pkgreen-700">
              Hardscaping in {town.name}
            </Link>
            <span className="text-pkstone-300">|</span>
            <Link to="/services/specialty-services" className="text-pkgreen-600 hover:text-pkgreen-700">
              Snow Removal in {town.name}
            </Link>
            {town.commercialFocus && (
              <>
                <span className="text-pkstone-300">|</span>
                <Link to="/commercial" className="text-pkgreen-600 hover:text-pkgreen-700">
                  Commercial Landscaping in {town.name}
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default TownPage;
